/** @jsx isolateComponent */

import { css } from '@emotion/core';
import { ReactNode } from 'react';

import { isolateComponent } from '@zapier/common-utils';
import { Margin, Surface } from '@zapier/design-system';

import {
  Currency,
  PlanType,
  PlanV2,
} from '@zapier/design-system-revenue/src/types';

import {
  IntroPricingActiveOfferBannerView,
  ReducedTaskTeamCompanyActiveOfferBannerView,
} from '@zapier/promotions/src/components/campaigns';

import { Offer } from '@zapier/promotions/src/types';
import { campaignSlugs } from '@zapier/promotions/src/utils/campaign-slugs';

import { StaticConfetti } from './StaticConfetti';

type ActiveOfferBannerProps = {
  currency: Currency;
  currentPlan: PlanV2;
  margin?: string;
  offer: Offer;
  offerEndDate: Date;
};

const rootStyles = css`
  overflow: hidden; /* prevents confetti from overflowing */
  position: relative; /* prevents hero image from covering this component */
`;

/**
 * A banner that shows the user the special offer plan they're on.
 */
export const ActiveOfferBanner = ({
  currency,
  currentPlan,
  margin = '0',
  offer,
  offerEndDate,
}: ActiveOfferBannerProps) => {
  const campaignSlug = offer?.variant?.campaign?.slug;

  if (!campaignSlug) {
    return null;
  }

  const getContent = (): ReactNode => {
    switch (campaignSlug) {
      case campaignSlugs.INTRO_STARTER: {
        const destinationPlan = offer?.variant?.destinationPlan;

        return destinationPlan ? (
          <IntroPricingActiveOfferBannerView
            currency={currency}
            destinationPlanAmount={destinationPlan?.amount}
            destinationPlanName={
              destinationPlan?.displayShortName ?? destinationPlan?.name
            }
            maxUses={offer?.variant?.campaign?.maxUses}
            offerEndDate={offerEndDate}
            totalCost={offer?.variant?.totalCost}
          />
        ) : null;
      }

      case campaignSlugs.TEAM_REDUCED_TASK:
      case campaignSlugs.COMPANY_REDUCED_TASK: {
        return (
          <ReducedTaskTeamCompanyActiveOfferBannerView
            currency={currency}
            interval={currentPlan.interval}
            monthlyAmount={currentPlan.amount}
            planType={currentPlan.type as PlanType}
            tasks={currentPlan.tasks}
          />
        );
      }

      default:
        return null;
    }
  };

  const content = getContent();

  return content ? (
    <Margin margin={margin}>
      <div css={rootStyles}>
        <Surface backgroundColor="blue100" borderColor="grey100">
          <StaticConfetti padding={20}>{content}</StaticConfetti>
        </Surface>
      </div>
    </Margin>
  ) : null;
};
